import { useState } from "react";
import {
  Modal,
  Typography,
  Upload,
  Button,
  Divider,
  Row,
  Col,
  message,
  Card,
  Spin,
  DatePicker,
  Form,
  Select,
  Tabs,
  Input,
} from "antd";
import {
  InboxOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_INVOICE_GENERAL, GET_LOCATIONS } from "../requests";
import useUpload from "../../../hooks/use-upload";
import moment from "moment-timezone";
import { typesUsers } from "../../../utils/constants";

const { Title } = Typography;
const { Dragger } = Upload;
const { MonthPicker } = DatePicker;

interface GeneralInvoiceModalProps {
  visible: boolean;
  onClose: () => void;
  user: any;
}

const GeneralInvoiceModal = ({
  visible,
  onClose,
  user,
}: GeneralInvoiceModalProps) => {
  const { upload } = useUpload();
  const [form] = Form.useForm();
  const [fileXml, setFileXml] = useState<string | null>(null);
  const [filePdf, setFilePdf] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [locationSearch, setLocationSearch] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>('files');
  const [folioNumber, setFolioNumber] = useState<string>('');

  // Consulta para obtener las ubicaciones
  const { data: locationsData } = useQuery(GET_LOCATIONS, {
    variables: {
      params: { page: 1,
        pageSize: 10 },
      name: locationSearch,
    },
    skip: user?.type === typesUsers.manager.value, // No cargar si es manager
  });

  const [createInvoiceGeneral] = useMutation(CREATE_INVOICE_GENERAL, {
    onCompleted: (data) => {
      if (data.createInvoiceGeneral.success) {
        message.success(data.createInvoiceGeneral.message);
      } else {
        message.error(data.createInvoiceGeneral.message);
      }
    },
    onError: (error) => {
      message.error(`Error al crear factura general: ${error.message}`);
    },
    refetchQueries: ['GetInvoices'],
  });

  // Opciones para el select de ubicaciones
  const locationOptions = locationsData?.locations?.results?.map((location: any) => ({
    label: location.name,
    value: location.id,
  })) || [];

  const handleFileUpload = async (file: File, type: "xml" | "pdf") => {
    setUploadLoading(true);
    try {
      const fileName = `general-invoices/${new Date().toISOString()}/${file.name}`;
      const url = await upload(file, fileName);
      message.success(`${type.toUpperCase()} cargado correctamente`);

      if (type === "xml") setFileXml(url);
      if (type === "pdf") setFilePdf(url);
    } catch {
      message.error(`Error al cargar el ${type}`);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFileDelete = (type: "xml" | "pdf") => {
    if (type === "xml") setFileXml(null);
    if (type === "pdf") setFilePdf(null);
    message.success(`${type.toUpperCase()} eliminado`);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const period = values.period;

      setLoading(true);

      const variables: any = {
        month: period.month() + 1,
        location: user?.type === typesUsers.manager.value ? user?.location?.id : values.location,
      };

      if (activeTab === 'files') {
        if (!fileXml || !filePdf) {
          throw new Error('Debe subir ambos archivos (XML y PDF)');
        }
        variables.xml = fileXml;
        variables.pdf = filePdf;
      } else {
        if (!folioNumber) {
          throw new Error('Debe ingresar un número de folio');
        }
        variables.reference = folioNumber;
      }

      await createInvoiceGeneral({ variables });

      onClose();
      form.resetFields();
      setFileXml(null);
      setFilePdf(null);
      setFolioNumber('');
    } catch (error) {
      message.error(
        (error && (error as Error).message)
          ? (error as Error).message
          : "Error al crear factura general",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Generar Factura General"
      visible={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          disabled={!( (fileXml && filePdf) || folioNumber )}
        >
          Generar Factura
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Card
          style={{
            boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
            borderRadius: "15px",
            marginBottom: 20,
          }}
        >
          <Title level={5} style={{ marginBottom: 20 }}>Periodo a Facturar</Title>

          <Form.Item
            name="period"
            label="Seleccione el mes y año"
            rules={[{ required: true,
              message: 'Por favor seleccione un periodo' }]}
          >
            <MonthPicker
              style={{ width: '100%' }}
              placeholder="Seleccione mes/año"
              format="MMMM YYYY"
              disabledDate={(current) => current && current > moment().endOf('month')}
            />
          </Form.Item>
        </Card>

        {user?.type !== typesUsers.manager.value && (
          <Card
            style={{
              boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
              borderRadius: "15px",
              marginBottom: 20,
            }}
          >
            <Title level={5} style={{ marginBottom: 20 }}>Sucursal a Facturar</Title>

            <Form.Item
              name="location"
              label="Seleccione la sucursal"
              rules={[{ required: true,
                message: 'Por favor seleccione una sucursal' }]}
            >
              <Select
                showSearch
                allowClear
                style={{ width: "100%" }}
                placeholder="Buscar sucursal..."
                options={[
                  { label: "Todas las sucursales",
                    value: "all" },
                  ...locationOptions,
                ]}
                filterOption={false}
                onSearch={(value) => setLocationSearch(value)}
                optionFilterProp="label"
              />
            </Form.Item>
          </Card>
        )}

        <Card
          style={{
            boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
            borderRadius: "15px",
          }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'files',
                label: 'Archivos',
                children: (
                  <>
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        {fileXml ? (
                          <div>
                            <Button
                              type="link"
                              icon={<FileTextOutlined />}
                              onClick={() => window.open(fileXml, "_blank")}
                            >
                              Ver XML
                            </Button>
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleFileDelete("xml")}
                              style={{ marginLeft: 8 }}
                            >
                              Eliminar
                            </Button>
                          </div>
                        ) : (
                          <Spin spinning={uploadLoading}>
                            <Dragger
                              beforeUpload={(file) => {
                                handleFileUpload(file, "xml");
                                return false;
                              }}
                              multiple={false}
                              accept=".xml"
                              showUploadList={false}
                              disabled={uploadLoading}
                            >
                              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                              <p>Haz clic o arrastra el archivo XML</p>
                              <p className="ant-upload-hint">Formato XML válido para facturación</p>
                            </Dragger>
                          </Spin>
                        )}
                      </Col>

                      <Col span={12}>
                        {filePdf ? (
                          <div>
                            <Button
                              type="link"
                              icon={<FilePdfOutlined />}
                              onClick={() => window.open(filePdf, "_blank")}
                            >
                              Ver PDF
                            </Button>
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleFileDelete("pdf")}
                              style={{ marginLeft: 8 }}
                            >
                              Eliminar
                            </Button>
                          </div>
                        ) : (
                          <Spin spinning={uploadLoading}>
                            <Dragger
                              beforeUpload={(file) => {
                                handleFileUpload(file, "pdf");
                                return false;
                              }}
                              multiple={false}
                              accept=".pdf"
                              showUploadList={false}
                              disabled={uploadLoading}
                            >
                              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                              <p>Haz clic o arrastra el archivo PDF</p>
                              <p className="ant-upload-hint">Representación PDF de la factura</p>
                            </Dragger>
                          </Spin>
                        )}
                      </Col>
                    </Row>
                    <Divider />
                    <Typography.Text type="secondary">
                      <ul>
                        <li>Suba ambos archivos (XML y PDF) para generar la factura general</li>
                        <li>El sistema validará que los documentos sean correctos</li>
                      </ul>
                    </Typography.Text>
                  </>
                ),
              },
              {
                key: 'folio',
                label: 'Número de Folio',
                children: (
                  <>
                    <Form.Item
                      name="folio"
                      label="Número de Folio"
                      rules={[
                        {
                          required: activeTab === 'folio',
                          message: 'Por favor ingrese el número de folio',
                        },
                        {
                          pattern: /^[A-Za-z0-9-]+$/,
                          message: 'Solo se permiten letras, números y guiones',
                        },
                      ]}
                    >
                      <Input
                        placeholder="Ej: ABC-123456"
                        value={folioNumber}
                        onChange={(e) => setFolioNumber(e.target.value)}
                      />
                    </Form.Item>
                    <Divider />
                    <Typography.Text type="secondary">
                      <ul>
                        <li>Ingrese el número de folio proporcionado por su sistema de facturación</li>
                        <li>El formato debe coincidir con su proveedor fiscal</li>
                      </ul>
                    </Typography.Text>
                  </>
                ),
              },
            ]}
          />
        </Card>
      </Form>
    </Modal>
  );
};

export default GeneralInvoiceModal;
